import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    TextField,
    MenuItem,
    Chip,
    IconButton,
} from "@mui/material";
import { styled } from "@mui/system";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CloseIcon from "@mui/icons-material/Close";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const BackgroundSection = styled(Box)(({ theme }) => ({
    position: "absolute",
    top: "0",
    left: "0",
    width: "100%",
    height: "80%",
    backgroundColor: "#E3EBD4",
    borderRadius: "0 0 60% 60%",
    zIndex: -1,
    [theme.breakpoints.down("sm")]: {
        height: "60%",
        borderRadius: "0 0 40% 40%",
    },
}));

const CarouselContainer = styled(Box)(() => ({
    position: "relative",
    backgroundSize: "cover",
    backgroundPosition: "center",
    borderRadius: "16px",
    overflow: "visible",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
    margin: "20px auto",
    maxWidth: "1200px",
    height: "400px",
    "@media (max-width: 600px)": {
        height: "250px",
        margin: "10px",
    },
}));

const HeaderContainer = styled(Box)(() => ({
    position: "absolute",
    top: "20px",
    left: "20px",
    display: "flex",
    flexDirection: "column",
    zIndex: 10,
    "@media (max-width: 600px)": {
        top: "10px",
    },
}));

const LearnMoreButton = styled(Button)(({ theme }) => ({
    position: "absolute",
    top: "16px", // Reduced top margin for better alignment
    right: "16px", // Adjusted right margin
    backgroundColor: "#ffffff",
    color: "#305c2f",
    fontWeight: "bold",
    fontSize: "14px", // Ensure font size is appropriate for normal screens
    padding: "6px 12px", // Adequate padding for normal screen size
    borderRadius: "15px",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)", // Add subtle shadow for better visibility
    "&:hover": {
        backgroundColor: "#e6e6e6",
    },
    // Media query for smaller screens
    "@media (max-width: 600px)": {
        top: "10px",
        right: "10px",
        fontSize: "12px",
        padding: "4px 8px",
        borderRadius: "12px",
    },
    // Media query for larger screens
    "@media (min-width: 1200px)": {
        top: "24px", // Slightly increase for extra-large screens
        right: "24px",
        fontSize: "16px",
        padding: "8px 16px",
    },
}));


const SearchFieldContainer = styled(Box)(({ theme }) => ({
    position: "absolute",
    bottom: "-35px",
    left: "5%",
    transform: "translateX(-50%)",
    backgroundColor: "#ffffff",
    padding: "20px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
    width: "90%",
    "@media (max-width: 600px)": {
        display: "none", // Hides the search box on mobile screens
    },
}));


const StyledTextField = styled(TextField)(() => ({
    marginRight: "10px",
    "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        height: "40px",
    },
}));

const ChipsContainer = styled(Box)(() => ({
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginLeft: "10px",
}));

const NavigationButton = styled(IconButton)(() => ({
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "white",
    "&:hover": {
        backgroundColor: "#f0f0f0",
    },
    zIndex: 10,
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
}));

const Carousel = () => {
    const slides = [
        {
            title: (
                <>
                    Family's <br /> Trusted Partner
                </>
            ),
            image: "https://i.postimg.cc/k5yfGHd7/image-2-1.png",
        },
        {
            title: (
                <>
                    Experience <br />  Excellence
                </>
            ),
            image: "https://i.postimg.cc/d3bzRbQP/Group-164-1.png",
        },
        // {
        //     title: (
        //         <>
        //             Your Trusted <br />  Choice
        //         </>
        //     ),
        //     image: "https://i.postimg.cc/k5yfGHd7/image-2-1.png",
        // },
    ];
    

    const [currentSlide, setCurrentSlide] = useState(0);
    const [services, setServices] = useState([]);
    const [selectedService, setSelectedService] = useState("");

    // Handle automatic sliding
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        }, 3000);
        return () => clearInterval(interval);
    }, [slides.length]);

    const handleNext = () => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
    };

    const handlePrev = () => {
        setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
    };

    const handleAddService = () => {
        if (selectedService && !services.includes(selectedService)) {
            setServices([...services, selectedService]);
            setSelectedService("");
        }
    };

    const handleRemoveService = (serviceToRemove) => {
        setServices(services.filter((service) => service !== serviceToRemove));
    };

    const handleScrollToProducts = () => {
        const productsDiv = document.getElementById("products");
        if (productsDiv) {
            productsDiv.scrollIntoView({ behavior: "smooth" });
        }
    };

    const navigate=useNavigate();

    const handleViewMoreClick = () => {
      navigate('/aboutus'); // Ensure this route exists in your router
    };


    return (
        <>
            <BackgroundSection />
            <CarouselContainer
                as={motion.div}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                style={{ backgroundImage: `url(${slides[currentSlide].image})` }}
            >
                <HeaderContainer
                    as={motion.div}
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    <Typography variant="h4" sx={{ fontWeight: "bold", color: "white", fontFamily: "'Montserrat', sans-serif", fontSize:"30px"}}>
                        {slides[currentSlide].title}
                    </Typography>
                </HeaderContainer>
                <LearnMoreButton
                    as={motion.div}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={handleViewMoreClick}
                    style={{cursor:"pointer"}}
                >
                    Learn More
                </LearnMoreButton>
                <NavigationButton onClick={handlePrev} sx={{ left: "10px" }}>
                    <ArrowBackIosIcon />
                </NavigationButton>
                <NavigationButton onClick={handleNext} sx={{ right: "10px" }}>
                    <ArrowForwardIosIcon />
                </NavigationButton>
                <SearchFieldContainer
                    as={motion.div}
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    <StyledTextField
                        style={{ width: "50%" }}
                        select
                        label="Services"
                        variant="outlined"
                        size="small"
                        value={selectedService}
                        onChange={(e) => setSelectedService(e.target.value)}
                    >
                        <MenuItem value="Hand Wash">Hand Wash</MenuItem>
                        <MenuItem value="Pickle">Pickle</MenuItem>
                        <MenuItem value="Dishwashing Liquid">
                            Dishwashing Liquid
                        </MenuItem>
                    </StyledTextField>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#a7c957",
                            color: "white",
                            borderRadius: "8px",
                            height: "40px",
                            "&:hover": { backgroundColor: "#8db947" },
                        }}
                        onClick={handleAddService}
                    >
                        Add
                    </Button>
                    <ChipsContainer>
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ duration: 0.4 }}
                            >
                                <Chip
                                    label={service}
                                    onDelete={() => handleRemoveService(service)}
                                    deleteIcon={<CloseIcon />}
                                    color="primary"
                                    variant="outlined"
                                />
                            </motion.div>
                        ))}
                    </ChipsContainer>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#305c2f",
                            color: "white",
                            borderRadius: "8px",
                            marginLeft: "auto",
                            height: "40px",
                            "&:hover": { backgroundColor: "#24491f" },
                        }}
                        onClick={handleScrollToProducts}
                    >
                        Search
                    </Button>
                </SearchFieldContainer>
            </CarouselContainer>
        </>
    );
};

export default Carousel;
